import { ContractTable } from '@features/contracts/components/ContractTable'
import { useContracts } from '@services/contracts/queires/useContracts'
import { FileText } from 'lucide-react'
import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import type { FlatDetail } from 'types/api'

import { Card } from '@/components/ui/card'

import { AttachmentSection } from './AttachmentSection'

function SectionCard({
  title,
  icon: Icon,
  children
}: {
  title: string
  icon: typeof FileText
  children: ReactNode
}) {
  return (
    <Card className="space-y-4 p-5">
      <div className="flex items-center gap-2.5">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Icon className="h-5 w-5" />
        </span>
        <h3 className="m-0 font-display text-base font-bold tracking-tight text-ink">
          {title}
        </h3>
      </div>
      {children}
    </Card>
  )
}

/** Read-only contracts + attachments for a landlord's flat. */
export function LandlordFlatContracts({ flat }: { flat: FlatDetail }) {
  const { t } = useTranslation('flat')
  const { data, isLoading } = useContracts({
    params: { type: 'flat', flat: flat.id }
  })

  if (isLoading) {
    return <div className="h-40 animate-pulse rounded-2xl bg-ink/[0.04]" />
  }

  const contracts = data?.results ?? []
  const hasContracts = contracts.length > 0

  const attachmentsByCategory = (category: 'AMENDMENT' | 'OTHER') =>
    contracts[0]?.attachments?.reduce(
      (acc: Record<string, unknown[]>, attachment) => {
        if (attachment.category === category) {
          acc.items = acc.items || []
          acc.items.push(attachment)
        }
        return acc
      },
      {}
    ) as never

  return (
    <div className="space-y-6">
      <SectionCard title={t('contract.card_title')} icon={FileText}>
        {hasContracts ? (
          <ContractTable flatId={flat.id} type="flat" readOnly />
        ) : (
          <div className="rounded-2xl border border-dashed border-ink/10 px-4 py-10 text-center text-sm text-muted-foreground">
            —
          </div>
        )}
      </SectionCard>

      {hasContracts && (
        <>
          <AttachmentSection
            attachments={attachmentsByCategory('AMENDMENT')}
            title={t('contract.amendments')}
            contract_id={contracts[0]?.id}
            category="AMENDMENT"
            type="flat"
            readOnly
          />
          <AttachmentSection
            attachments={attachmentsByCategory('OTHER')}
            title={t('contract.other')}
            contract_id={contracts[0]?.id}
            category="OTHER"
            type="flat"
            readOnly
          />
        </>
      )}
    </div>
  )
}
